import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { FEATURES, HOW_IT_WORKS_SHARING, HOW_IT_WORKS_FOLLOWING, APP_CONFIG } from '../config/constants';
import DownloadModal from '../components/DownloadModal';

const HeroSection = styled.section`
  background: transparent;
  color: white;
  padding: 8rem 2rem;
  text-align: center;
  position: relative;
  overflow: hidden;
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
`;

const HeroTitle = styled.h1`
  font-size: 4.5rem;
  margin-bottom: 2rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.1;
  color: rgba(255, 255, 255, 0.95);
  
  @media (max-width: 768px) {
    font-size: 3rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.5rem;
  margin-bottom: 3rem;
  color: rgba(255, 255, 255, 0.8);
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
  font-weight: 400;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const HowItWorksSection = styled.section`
  background: transparent;
  position: relative;
  padding: 6rem 0;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 300px;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  }
`;

const TabContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 4rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 0.75rem;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
`;

const TabButton = styled.button`
  flex: 1;
  padding: 1.25rem 2rem;
  border: none;
  background: ${props => props.$active ? 'linear-gradient(135deg, #7359B3 0%, #593399 100%)' : 'transparent'};
  color: ${props => props.$active ? 'white' : 'rgba(255, 255, 255, 0.7)'};
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 1.1rem;
  position: relative;
  overflow: hidden;

  &:hover {
    background: ${props => props.$active ? 'linear-gradient(135deg, #7359B3 0%, #593399 100%)' : 'rgba(255, 255, 255, 0.1)'};
    color: ${props => props.$active ? 'white' : 'rgba(255, 255, 255, 0.95)'};
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    padding: 1rem 1.5rem;
    font-size: 1rem;
  }
`;

const TimelineContainer = styled.div`
  max-width: 800px;
  margin: 3rem auto 0;
  position: relative;
`;

const TimelineLine = styled.div`
  position: absolute;
  left: 2rem;
  width: 4px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0.5) 100%);
  border-radius: 2px;
  box-shadow: 0 0 10px rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    left: 1.5rem;
  }
`;

const Step = styled.div`
  position: relative;
  margin-bottom: 4rem;
  display: flex;
  align-items: flex-start;
  gap: 2rem;

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    gap: 1.5rem;
    margin-bottom: 3rem;
  }
`;

const StepIndicator = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 4rem;
  height: 4rem;
  background: linear-gradient(135deg, #7359B3 0%, #593399 100%);
  color: white;
  border-radius: 50%;
  font-size: 1.5rem;
  font-weight: 700;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  border: 4px solid rgba(255, 255, 255, 0.2);
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 3rem;
    height: 3rem;
    font-size: 1.25rem;
  }
`;

const StepContent = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: 2.5rem;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  flex: 1;
  position: relative;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.3);
    border-color: rgba(255, 255, 255, 0.3);
    background: rgba(255, 255, 255, 0.12);
  }

  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const StepTitle = styled.h3`
  margin-bottom: 1.25rem;
  color: rgba(255, 255, 255, 0.95);
  font-size: 1.4rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.3;
`;

const StepDescription = styled.p`
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.7;
  margin: 0;
  font-size: 1.05rem;
  font-weight: 400;
`;

const FeaturesSection = styled.section`
  background: transparent;
  position: relative;
  padding: 6rem 0;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 300px;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2.5rem;
  margin-top: 4rem;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const Feature = styled.div`
  padding: 2.5rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  text-align: left;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.3);
    border-color: rgba(255, 255, 255, 0.3);
    background: rgba(255, 255, 255, 0.12);
  }
  
  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const FeatureTitle = styled.h3`
  margin-bottom: 1rem;
  color: rgba(255, 255, 255, 0.95);
  font-size: 1.4rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.2;
  margin-bottom: 1.25rem;
`;

const FeatureDescription = styled.p`
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.7;
  font-size: 1.05rem;
  font-weight: 400;
  margin: 0;
`;


const Home = () => {
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('sharing');
  const [timelineStyle, setTimelineStyle] = useState({});
  const timelineRef = useRef(null);
  const firstIndicatorRef = useRef(null);
  const lastIndicatorRef = useRef(null);

  const currentSteps = activeTab === 'sharing' ? HOW_IT_WORKS_SHARING : HOW_IT_WORKS_FOLLOWING;

  useEffect(() => {
    const updateTimelinePosition = () => {
      if (firstIndicatorRef.current && lastIndicatorRef.current && timelineRef.current) {
        const firstRect = firstIndicatorRef.current.getBoundingClientRect();
        const lastRect = lastIndicatorRef.current.getBoundingClientRect();
        const timelineRect = timelineRef.current.getBoundingClientRect();
        
        const startY = firstRect.top - timelineRect.top + (firstRect.height / 2);
        const endY = lastRect.top - timelineRect.top + (lastRect.height / 2);
        
        setTimelineStyle({
          top: `${startY}px`,
          height: `${endY - startY}px`
        });
      }
    };

    // Update on mount and when tab changes
    updateTimelinePosition();
    
    // Update on window resize
    window.addEventListener('resize', updateTimelinePosition);
    
    return () => window.removeEventListener('resize', updateTimelinePosition);
  }, [activeTab]);

  return (
    <>
      <HeroSection>
        <div className="container">
          <HeroContent>
            <HeroTitle>Automated Check-ins, Peace of Mind</HeroTitle>
            <HeroSubtitle>
              No texts, calls, or location tracking. Just automatic daily signals that say "I'm okay"
            </HeroSubtitle>
            <button className="cta-button" onClick={() => setIsDownloadModalOpen(true)}>
              Get Started
            </button>
          </HeroContent>
        </div>
      </HeroSection>

      <HowItWorksSection>
        <div className="container">
          <h2 style={{ 
            fontSize: '3rem', 
            fontWeight: '700', 
            textAlign: 'center', 
            marginBottom: '1rem',
            letterSpacing: '-0.02em',
            lineHeight: '1.1',
            color: 'rgba(255, 255, 255, 0.95)'
          }}>How It Works</h2>
          <p style={{
            fontSize: '1.25rem',
            textAlign: 'center',
            color: 'rgba(255, 255, 255, 0.8)',
            maxWidth: '600px',
            margin: '0 auto 3rem',
            lineHeight: '1.6'
          }}>Simple steps to get peace of mind for you and your loved ones</p>
          <TabContainer>
            <TabButton 
              $active={activeTab === 'sharing'} 
              onClick={() => setActiveTab('sharing')}
            >
              I Want to Provide Peace of Mind
            </TabButton>
            <TabButton 
              $active={activeTab === 'following'} 
              onClick={() => setActiveTab('following')}
            >
              I Want Peace of Mind
            </TabButton>
          </TabContainer>
          <TimelineContainer ref={timelineRef}>
            <TimelineLine style={timelineStyle} />
            {currentSteps.map((step, index) => (
              <Step key={step.number}>
                <StepIndicator 
                  ref={index === 0 ? firstIndicatorRef : index === currentSteps.length - 1 ? lastIndicatorRef : null}
                >
                  {step.number}
                </StepIndicator>
                <StepContent>
                  <StepTitle>{step.title}</StepTitle>
                  <StepDescription>{step.description}</StepDescription>
                </StepContent>
              </Step>
            ))}
          </TimelineContainer>
        </div>
      </HowItWorksSection>

      <FeaturesSection>
        <div className="container">
          <h2 style={{ 
            fontSize: '3rem', 
            fontWeight: '700', 
            textAlign: 'center', 
            marginBottom: '1rem',
            letterSpacing: '-0.02em',
            lineHeight: '1.1',
            color: 'rgba(255, 255, 255, 0.95)'
          }}>Why Use {APP_CONFIG.name}?</h2>
          <p style={{
            fontSize: '1.25rem',
            textAlign: 'center',
            color: 'rgba(255, 255, 255, 0.8)',
            maxWidth: '600px',
            margin: '0 auto',
            lineHeight: '1.6'
          }}>Everything you need for effortless peace of mind</p>
          <FeaturesGrid>
            {FEATURES.map((feature, index) => (
              <Feature key={index}>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
              </Feature>
            ))}
          </FeaturesGrid>
        </div>
      </FeaturesSection>
      
      <DownloadModal 
        isOpen={isDownloadModalOpen} 
        onClose={() => setIsDownloadModalOpen(false)} 
      />
    </>
  );
};

export default Home;
